import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { CircleDollarSign, Users, AlertTriangle, Clock, TrendingUp } from "lucide-react";

const BUCKETS = [
  { key: "current", label: "Current", color: "#0ea5e9" },
  { key: "1-30", label: "1–30 days", color: "#f59e0b" },
  { key: "31-60", label: "31–60 days", color: "#f97316" },
  { key: "61-90", label: "61–90 days", color: "#ef4444" },
  { key: "90+", label: "90+ days", color: "#be123c" },
];

export default function ARAging() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ar/aging").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  const chartData = BUCKETS.map((b) => ({ name: b.label, value: data.buckets[b.key], color: b.color }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold">Accounts Receivable Aging</h1>
        <p className="text-muted-foreground">Everything owed to the business, grouped by how overdue it is.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={CircleDollarSign} label="Total Owed" value={money(data.total_owed)} color="text-violet-500" />
        <Kpi icon={AlertTriangle} label="Total Overdue" value={money(data.total_overdue)} color="text-rose-500" />
        <Kpi icon={Users} label="Customers Owing" value={data.customers_owing} color="text-sky-500" />
        <Kpi icon={Clock} label="Approaching Due" value={data.approaching_due.length} color="text-amber-500" />
      </div>
      {data.customer_credits > 0 && (
        <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 text-sm" data-testid="ar-customer-credits">
          <p className="font-semibold text-sky-600">Customer Credits / Credit Balances: {money(data.customer_credits)}</p>
          <p className="text-xs text-muted-foreground">Overpayments or credit balances owed <b>to</b> customers. Tracked separately and never deducted from Accounts Receivable.</p>
          <ul className="mt-2 text-xs space-y-0.5">{data.credit_records.map((r) => <li key={r.invoice_number} className="mono">{r.invoice_number} · {r.customer_name} · {money(r.amount)}</li>)}</ul>
        </div>
      )}

      {/* Aging buckets */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {BUCKETS.map((b) => (
          <Card key={b.key} className="p-4" style={{ borderColor: `${b.color}44` }} data-testid={`bucket-${b.key}`}>
            <div className="w-2.5 h-2.5 rounded-full mb-2" style={{ background: b.color }} />
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</p>
            <p className="mono text-lg font-bold mt-1">{money(data.buckets[b.key])}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <Card className="p-5">
          <p className="font-head font-bold mb-4">Aging Distribution</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <p className="font-head font-bold mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Most Overdue</p>
          <div className="space-y-2">
            {data.most_overdue.map((c) => (
              <button key={c.id} onClick={() => nav(`/customers/${c.id}`)} data-testid={`overdue-${c.id}`}
                className="w-full flex justify-between items-center p-2.5 rounded-lg hover:bg-secondary text-left">
                <div><p className="text-sm font-medium">{c.name}</p><p className="text-xs text-rose-500">{c.max_days_overdue} days overdue</p></div>
                <span className="mono font-bold text-sm">{money(c.balance)}</span>
              </button>
            ))}
            {data.most_overdue.length === 0 && <p className="text-sm text-muted-foreground">No overdue accounts.</p>}
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <p className="font-head font-bold p-4 border-b border-border">All Owing Customers (click to drill into invoices)</p>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Customer</TableHead><TableHead>Contact</TableHead>
            <TableHead className="text-right">Overdue</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.all_owing.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => nav(`/customers/${c.id}`)} data-testid={`owing-row-${c.id}`}>
                <TableCell><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.company}</p></TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.phone}</TableCell>
                <TableCell className="text-right mono text-rose-500">{money(c.overdue)}</TableCell>
                <TableCell className="text-right mono font-bold">{money(c.balance)}</TableCell>
                <TableCell>{c.max_days_overdue > 0 ? <Badge className="bg-rose-500/15 text-rose-500 border-rose-500/30">{c.max_days_overdue}d</Badge> : <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Current</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {data.approaching_due.length > 0 && (
        <Card className="p-5">
          <p className="font-head font-bold mb-3 flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-500" /> Customers Approaching Due Date</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.approaching_due.map((a) => (
              <div key={a.invoice_number} className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
                <p className="font-medium text-sm">{a.customer_name}</p>
                <p className="text-xs text-muted-foreground mono">{a.invoice_number} · due {fmtDate(a.due_date)}</p>
                <div className="flex justify-between mt-1"><span className="mono font-bold text-sm">{money(a.remaining_balance)}</span><Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">in {a.due_in}d</Badge></div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="p-2.5 rounded-xl bg-secondary"><Icon className={`w-5 h-5 ${color}`} /></div>
      <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="mono text-xl font-bold">{value}</p></div>
    </Card>
  );
}
